import React from 'react';
import { ArrowLeft, Clock, Users, CheckCircle2, MessageCircle, ArrowRight } from 'lucide-react';

interface WorkshopSectionProps {
  onBack?: () => void;
}

const WorkshopSection: React.FC<WorkshopSectionProps> = ({ onBack }) => {
  const CALENDLY_LINK = "https://calendly.com/lab_z/virtual-coffee-discovery-20-minutes";

  return (
    <section className="min-h-screen py-32 md:py-48 bg-cream text-charcoal px-6 relative overflow-hidden">
      <div className="max-w-6xl mx-auto relative z-10">
        {onBack && (
          <button 
            onClick={onBack}
            className="mb-12 flex items-center gap-2 text-neutral-400 hover:text-terracotta transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">Return Home</span>
          </button>
        )}

        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7 space-y-16">
            <div className="space-y-6">
              <span className="text-terracotta text-[10px] font-black uppercase tracking-[0.5em] block">One-Day Intensive</span>
              <h1 className="text-6xl md:text-[6rem] leading-[0.85] tracking-tighter font-black text-charcoal">
                The Meaning <br/>
                <span className="text-neutral-400 italic">Shift</span> <br/>
                Workshop.
              </h1>
              <p className="text-xl md:text-2xl font-bold tracking-tight text-charcoal pt-4">
                Working With AI, Not Chasing It.
              </p>
            </div>

            <div className="space-y-10 text-lg md:text-xl text-neutral-800 leading-relaxed font-light">
              <div className="space-y-6">
                <p>
                  AI is moving fast. Understanding is not. Most teams are under pressure to “use AI.” They add tools. They automate tasks. They move faster.
                </p>
                <p className="font-bold text-charcoal underline decoration-terracotta/30">
                  But speed without shared understanding creates noise, not momentum.
                </p>
                <p>
                  The real problem isn’t access to AI. It’s knowing where AI actually matters.
                </p>
              </div>

              <div className="p-8 bg-white border border-neutral-100 rounded-xl space-y-6 shadow-sm">
                <h3 className="text-2xl font-black tracking-tight text-charcoal">The Meaning Shift</h3>
                <p>
                  This workshop reframes AI from a productivity upgrade into a meaning, judgment, and leadership challenge.
                </p>
                <p>
                  When AI is treated as a shortcut, trust erodes and decisions stall. When it’s treated as a co-intelligence, teams gain leverage, alignment, and confidence.
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <h3 className="text-[10px] font-black uppercase tracking-[0.5em] text-terracotta">What Participants Leave With</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  "A shared language for AI inside their organization",
                  "Clear priorities grounded in real constraints",
                  "Confidence to work with AI without waiting for experts",
                  "One or two usable AI initiatives to apply immediately",
                  "A practical way to evaluate impact versus effort",
                  "A repeatable approach for long-term growth"
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <CheckCircle2 size={18} className="text-terracotta shrink-0 mt-1" />
                    <p className="text-sm font-black text-charcoal leading-snug">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-12">
            <div className="bg-[#1A1A1A] text-white p-10 md:p-12 rounded-2xl shadow-[0_40px_80px_-15px_rgba(0,0,0,0.5)] relative overflow-hidden border border-white/5">
               <div className="absolute top-0 right-0 p-8 opacity-10">
                <Clock size={120} />
              </div>
              
              <div className="relative z-10 space-y-10">
                <h3 className="text-3xl font-black italic text-charcoal bg-white -mx-10 md:-mx-12 px-10 md:px-12 py-4">The Day at a Glance.</h3>
                
                <div className="space-y-12">
                  {[
                    {
                      num: "01",
                      title: "Orientation: The Meaning Shift",
                      desc: "Align on what AI is actually for. Explore why efficiency alone fails and introduce a framework for co-intelligence."
                    },
                    {
                      num: "02",
                      title: "Signal Discovery",
                      desc: "Using a guided sailboat exercise, teams surface momentum drivers and quiet hold-backs to prioritize what’s worth working on."
                    },
                    {
                      num: "03",
                      title: "Guided Exploration With AI",
                      desc: "Learn to think with AI, not just prompt it. Small groups explore real challenges focusing on judgment and framing."
                    },
                    {
                      num: "04",
                      title: "Convergence and Commitment",
                      desc: "Develop promising ideas into usable outputs. Close with reflection and clear next steps for continued momentum."
                    }
                  ].map((step, i) => (
                    <div key={i} className="flex gap-6 group">
                      <span className="text-terracotta text-sm font-black mt-1 shrink-0">{step.num}</span>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-black uppercase tracking-widest text-white border-b border-terracotta/30 pb-1 inline-block mb-1">{step.title}</h4>
                        <p className="text-[14px] text-white font-medium leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 p-10 rounded-2xl shadow-sm space-y-8">
              <div className="space-y-4">
                <h3 className="text-2xl font-black tracking-tight text-charcoal">Ready for Clarity?</h3>
                <p className="text-sm text-neutral-800 leading-relaxed font-bold">
                  Typically for small to mid-sized teams (5–30).
                </p>
              </div>
              
              <div className="space-y-4">
                <a 
                  href={CALENDLY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-6 bg-terracotta text-white px-8 py-5 rounded-lg shadow-lg hover:bg-[#A85B4E] hover:scale-[1.02] transition-all w-full"
                >
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">Schedule a Clarity Session</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-20 -right-20 text-[18rem] font-black text-neutral-900/[0.03] select-none pointer-events-none leading-none rotate-3">
        SHIFT
      </div>
    </section>
  );
};

export default WorkshopSection;